import {
  Dialog, DialogActions,
} from '@mui/material';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { NetworkIcon } from 'elements/NetworkIcon';
import { NETWORK_IDS, NETWORKS, POSTING_ACCOUNT_TYPE } from 'lib/constants';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import {
  extractUsernameFromPostingAccountUrl, looksLikeUrl,
  trimLeadingAtSign, userIsAdmin,
} from 'lib/helpers';
import { PostingAccountType } from 'lib/types/postingAccounts';
import { User } from 'lib/types/users';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import { useState, useCallback, ChangeEvent } from 'react';
import { ConnectButton } from './ConnectButton';

interface Props {
  network: number;
  user: User;
  onClose: () => void;
}

const authSupportedNetworks = [
  NETWORK_IDS.facebook,
  NETWORK_IDS.instagram,
  NETWORK_IDS.tiktok,
  NETWORK_IDS.twitter,
  NETWORK_IDS.twitch,
  NETWORK_IDS.youtube,
  NETWORK_IDS.linkedin,
  NETWORK_IDS.threads,
];

export const AddSocialNetworkModal = ({
  network, onClose, user,
}: Props) => {
  const networkTitle = NETWORKS[network].title;
  const API = useApi();
  const currentUser = useCurrentUser();
  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const networkIcon = NetworkIcon({ network, iconColor: 'color', className: 'ml-2' });
  const [account, setAccount] = useState('');
  const [type, setType] = useState<PostingAccountType>(POSTING_ACCOUNT_TYPE.owner);
  const [loading, setLoading] = useState(false);

  /** Users may be adding accounts for themselves or for a team account */
  const isCurrentUser = currentUser?.id === user.id;

  /**
   * Linkedin is a special case. We don't want to show the connect button to non-admin users.
   */
  const isAuthSupportedNetwork = (n: number) => {
    if (!authSupportedNetworks.includes(n)) {
      return false;
    }
    if (n === NETWORK_IDS.linkedin && !userIsAdmin(user)) {
      return false;
    }
    return true;
  };

  const handleAccountChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAccount(e.target.value);
  }, []);

  const handleTypeChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setType(e.target.value as PostingAccountType);
  }, []);

  const savePostingAccount = useCallback(async (cleanAccount: string) => {
    try {
      setLoading(true);

      await API.addPostingAccount({
        network, account: cleanAccount, userId: user.id, type,
      });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(`Error adding account @${account} on ${networkTitle}: ${err.message}`, { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API, network, user.id, type, account, networkTitle, onClose, enqueueSnackbar]);

  const handleConnectComplete = useCallback(() => {
    onClose();
  }, [onClose]);

  const addAccount = async () => {
    const cleanAccount = trimLeadingAtSign(account) as string;
    if (looksLikeUrl(cleanAccount)) {
      try {
        const username = extractUsernameFromPostingAccountUrl(network, cleanAccount) as string;
        const modal = showModal(ConfirmationModal, {
          text: (
            <>
              We detected a URL, is
              {' '}
              <strong>
                @
                {username}
              </strong>
              {' '}
              the correct username for
              {' '}
              {isCurrentUser ? 'your' : `${user.name}'s`}
              {' '}
              {NETWORKS[network].title}
              {' '}
              account?
            </>
          ),
          title: 'Confirm username',
          onNo: () => {
            enqueueSnackbar(
              'Please enter a valid username and try again.', {
                variant: 'error',
              },
            );
            modal.destroy();
          },
          onYes: async () => {
            await savePostingAccount(username);
            modal.destroy();
          },
        });
      } catch (err) {
        enqueueSnackbar(
          'An error occurred please check the username you entered and try again.', {
            variant: 'error',
          },
        );
      }
    } else {
      const newAccountMatchesExisting = await API.checkForMatchingAccount(cleanAccount);
      if (newAccountMatchesExisting || type === POSTING_ACCOUNT_TYPE.manager) {
        await savePostingAccount(cleanAccount);
      } else {
        const modal = showModal(ConfirmationModal, {
          text: (
            <>
              The username you&apos;re trying to add doesn&apos;t match your own or any of your
              connected accounts. Do you confirm this is your personal account
              and not one you manage or an unrelated one?
            </>
          ),
          title: 'Confirm account',
          onNo: () => {
            modal.destroy();
          },
          onYes: async () => {
            await savePostingAccount(cleanAccount);
            modal.destroy();
          },
        });
      }
    }
  };

  return (
    <Dialog open maxWidth="md" className="w-full" fullWidth>
      <div className="p-6">
        <LoadingOverlay open={loading} text="Adding account..." />
        <div className="relative mb-6">
          <div className="flex items-center mb-2 ">
            {networkIcon}
            <span className="ml-2 font-bold">
              Add
              {' '}
              {networkTitle}
              {' '}
              account
            </span>
          </div>
        </div>
        {isAuthSupportedNetwork(network) && (
          <div className="text-sm grid grid-cols-4 gap-2 my-2">
            <p className="text-sm col-span-2 md:col-span-3 align-middle">
              If you would like to set up automatic importing and crediting,
              you can authorize Gondola to access this account.

            </p>
            <div className="col-span-2 md:col-span-1 align-middle">
              <ConnectButton network={network} defaultLabel="Connect" onConnect={handleConnectComplete} />
            </div>
            OR
          </div>
        )}
        <p className="text-sm mb-2">
          Please enter
          {' '}
          {isCurrentUser ? 'your' : `${user.name}'s`}
          {' '}
          <strong>
            {NETWORKS[network].title}
          </strong>
          {' '}
          username
        </p>
        <input
          className="text-sm p-2 rounded-lg border outline-none w-full"
          id="username"
          placeholder={network === NETWORK_IDS.linkedin
            ? `${NETWORKS[network].title} username with prefix (e.g. in/john-doe or company/acme-inc)`
            : `${NETWORKS[network].title} username ex: @${user.username}`}
          type="search"
          onChange={handleAccountChange}
        />
        {isCurrentUser && (
          <div className="grid grid-cols-4 text-sm gap-2 mt-4 mb-4">
            <p className="text-sm col-span-2 md:col-span-3 align-middle">
              Is this your own personal account or an account you manage?
            </p>
            <select
              className="text-xs p-2 w-22 rounded-lg outline-none border col-span-2 md:col-span-1 align-middle max-h-10"
              defaultValue={POSTING_ACCOUNT_TYPE.owner}
              onChange={handleTypeChange}
            >
              <option value={POSTING_ACCOUNT_TYPE.owner}>Personal</option>
              <option value={POSTING_ACCOUNT_TYPE.manager}>Managed/Business</option>
            </select>
          </div>
        )}
        <DialogActions>
          <button
            onClick={onClose}
            color="inherit"
            type="button"
            className="btn-primary-outlined mr-2"
          >
            Close
          </button>
          <button
            onClick={addAccount}
            className="btn-primary ml-2"
            color="primary"
            type="button"
            disabled={!account}
          >
            Save
          </button>
        </DialogActions>
      </div>
    </Dialog>
  );
};
